import { ListWithPreviewInfiniteScroller } from "@/features/common/components/dataview/ListWithPreviewInfiniteScroller";
import { screenSizeIsConsideredMobile, useScreenSize } from "@/lib/common/ScreenSizeContext";
import type { PaginatedResponseModel, ProblemResponse } from "@/lib/http";
import type { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useMemo, useRef } from "react";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";

export interface ListWithPreviewProps<T> {
	children: React.ReactNode;
	forQuery: UseInfiniteQueryResult<InfiniteData<PaginatedResponseModel<T>>, ProblemResponse>;
	value: T | null;
	onChange: (value: T | null) => void;
	getKey: (item: T) => React.Key;
	componentFunction: React.ComponentType<T & ListWithPreviewItemProps>;
}

export interface ListWithPreviewItemProps {
	isItemSelected: boolean;
	onItemSelected: () => void;
}

export const ListWithPreview = <T,>(props: ListWithPreviewProps<T>) => {
	const {
		children,
		forQuery,
		value,
		onChange,
		getKey,
		componentFunction: ComponentFunction
	} = props;

	const screenSize = useScreenSize();
	const items = useMemo(() => forQuery.data?.pages.flatMap(x => x.results) ?? [], [forQuery.data]);
	const ref = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (value === null || items.includes(value)) {
			return;
		}

		const matchedItem = items.find(x => getKey(x) === getKey(value));
		onChange(matchedItem || null);
	}, [items]);

	return (
		<SplitScreen fixed visible={!!value} onVisibleChange={() => onChange(null)}>
			<SplitScreenLeftPanel ref={ref}>
				{
					items.map(item => (
						<ComponentFunction onItemSelected={() => onChange(item)} isItemSelected={!!value && item === value} key={getKey(item)} {...item} />
					))
				}

				<ListWithPreviewInfiniteScroller forQuery={forQuery} scrollRef={!screenSizeIsConsideredMobile(screenSize) ? ref : undefined} />
			</SplitScreenLeftPanel>

			<SplitScreenRightPanel>
				{children}
			</SplitScreenRightPanel>
		</SplitScreen>
	);
};
